import { supabase } from './supabase';

export interface SampleDataPreferences {
  showSampleData: boolean;
  showSampleDataPrompts: boolean;
  lastPromptDate?: string;
  dismissedFeatures: string[];
}

export interface SampleDataContext {
  feature: string;
  description: string;
  benefits: string[];
  sampleDataTypes: string[];
}

// Sample data contexts for different features
export const SAMPLE_DATA_CONTEXTS: Record<string, SampleDataContext> = {
  'food-tracking': {
    feature: 'Food Tracking',
    description: 'See how nutrition tracking works with sample foods',
    benefits: [
      'Learn how to log meals and snacks',
      'See nutrition analysis in action',
      'Understand barcode scanning features'
    ],
    sampleDataTypes: ['usda_foods', 'food_entries']
  },
  'medication-management': {
    feature: 'Medication Management',
    description: 'Explore medication tracking and reminders',
    benefits: [
      'See how to add and manage medications',
      'Learn about adherence tracking',
      'Understand medication interactions'
    ],
    sampleDataTypes: ['fda_medications', 'medications']
  },
  'health-metrics': {
    feature: 'Health Metrics',
    description: 'View sample health data and trends',
    benefits: [
      'See how metrics are displayed',
      'Learn about trend analysis',
      'Understand goal setting'
    ],
    sampleDataTypes: ['device_data', 'symptoms']
  },
  'guru-connect': {
    feature: 'Guru Connect',
    description: 'Explore health coaching and professional guidance',
    benefits: [
      'See how to connect with health professionals',
      'Learn about session booking',
      'Understand content discovery'
    ],
    sampleDataTypes: ['guru_profiles', 'sessions']
  }
};

class SampleDataManager {
  private readonly STORAGE_KEY = 'zippyhealth_sample_data_preferences';
  private readonly PROMPT_COOLDOWN_DAYS = 7;

  // Get user preferences for sample data
  async getUserPreferences(): Promise<SampleDataPreferences> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        return this.getDefaultPreferences();
      }

      // Use localStorage as primary storage method
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        return JSON.parse(stored);
      }

      return this.getDefaultPreferences();
    } catch (error) {
      console.error('Error getting sample data preferences:', error);
      return this.getDefaultPreferences();
    }
  }

  // Save user preferences
  async saveUserPreferences(preferences: SampleDataPreferences): Promise<void> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(preferences));
        return;
      }

      // Save to localStorage as primary storage
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(preferences));
    } catch (error) {
      console.error('Error saving sample data preferences:', error);
      // Fallback to localStorage
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(preferences));
    }
  }

  // Check if we should show sample data prompt for a feature
  async shouldShowPrompt(feature: string): Promise<boolean> {
    const preferences = await this.getUserPreferences();
    
    // If user has disabled prompts, don't show
    if (!preferences.showSampleDataPrompts) {
      return false;
    }

    // If user has dismissed this feature, don't show
    if (preferences.dismissedFeatures.includes(feature)) {
      return false;
    }

    // Check cooldown period
    if (preferences.lastPromptDate) {
      const lastPrompt = new Date(preferences.lastPromptDate);
      const daysSinceLastPrompt = (Date.now() - lastPrompt.getTime()) / (1000 * 60 * 60 * 24);
      if (daysSinceLastPrompt < this.PROMPT_COOLDOWN_DAYS) {
        return false;
      }
    }

    return true;
  }

  // Show sample data prompt for a feature
  async showSampleDataPrompt(feature: string): Promise<boolean> {
    const context = SAMPLE_DATA_CONTEXTS[feature];
    if (!context) {
      console.warn(`No sample data context found for feature: ${feature}`);
      return false;
    }

    const shouldShow = await this.shouldShowPrompt(feature);
    if (!shouldShow) {
      return false;
    }

    // Update last prompt date
    const preferences = await this.getUserPreferences();
    preferences.lastPromptDate = new Date().toISOString();
    await this.saveUserPreferences(preferences);

    return true;
  }

  // Dismiss sample data prompt for a feature
  async dismissSampleDataPrompt(feature: string): Promise<void> {
    const preferences = await this.getUserPreferences();
    if (!preferences.dismissedFeatures.includes(feature)) {
      preferences.dismissedFeatures.push(feature);
      await this.saveUserPreferences(preferences);
    }
  }

  // Enable/disable sample data globally
  async setSampleDataEnabled(enabled: boolean): Promise<void> {
    const preferences = await this.getUserPreferences();
    preferences.showSampleData = enabled;
    await this.saveUserPreferences(preferences);
  }

  // Enable/disable sample data prompts
  async setSampleDataPromptsEnabled(enabled: boolean): Promise<void> {
    const preferences = await this.getUserPreferences();
    preferences.showSampleDataPrompts = enabled;
    await this.saveUserPreferences(preferences);
  }

  // Check if sample data should be shown for a feature
  async shouldShowSampleData(feature: string): Promise<boolean> {
    const preferences = await this.getUserPreferences();
    return preferences.showSampleData;
  }

  // Get sample data context for a feature
  getSampleDataContext(feature: string): SampleDataContext | null {
    return SAMPLE_DATA_CONTEXTS[feature] || null;
  }

  // Get all available sample data contexts
  getAllSampleDataContexts(): SampleDataContext[] {
    return Object.values(SAMPLE_DATA_CONTEXTS);
  }

  // Reset all sample data preferences
  async resetPreferences(): Promise<void> {
    const defaultPreferences = this.getDefaultPreferences();
    await this.saveUserPreferences(defaultPreferences);
  }

  private getDefaultPreferences(): SampleDataPreferences {
    return {
      showSampleData: false,
      showSampleDataPrompts: true,
      dismissedFeatures: []
    };
  }
}

export const sampleDataManager = new SampleDataManager(); 